import React, { useEffect, useMemo } from 'react';
import {
  PlatformProvider,
  ARCHIE_WEB,
  ARCHIE_MAC,
  MARTHA_WEB,
  MARTHA_MAC,
  type BrandProfileData,
} from '@archieai/ui';
import { usePreferences } from '../contexts/PreferencesContext';

const BRAND_MAP: Record<string, BrandProfileData> = {
  'martha:web': MARTHA_WEB,
  'martha:mac': MARTHA_MAC,
  'archie:web': ARCHIE_WEB,
  'archie:mac': ARCHIE_MAC,
};

// Tailwind palette stops we tokenise in tailwind.config.js
const TAILWIND_STOPS = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900', '950'] as const;

type RGB = [number, number, number];

function hexToRgb(hex: string): RGB | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const raw = m[1].length === 3 ? m[1].split('').map(c => c + c).join('') : m[1];
  return [
    parseInt(raw.slice(0, 2), 16),
    parseInt(raw.slice(2, 4), 16),
    parseInt(raw.slice(4, 6), 16),
  ];
}

const tripletStr = ([r, g, b]: RGB) => `${r} ${g} ${b}`;

// Brand palettes are sparse (Archie ships only 200/400/800/1000 for primary,
// 50/100/200/600/800/1000 for neutral). Linearly interpolate in RGB between the
// two defined stops bracketing each Tailwind target so 300/400/500/700/900 are
// real intermediate colors instead of duplicates of the nearest defined stop.
function interpolatePalette(palette: Record<string, string> | undefined): Map<number, RGB> {
  const out = new Map<number, RGB>();
  if (!palette) return out;
  const entries: { n: number; rgb: RGB }[] = [];
  for (const [k, v] of Object.entries(palette)) {
    if (typeof v !== 'string') continue;
    // Some palettes use '1e3' for 1000; also tolerate non-numeric keys like 'white'.
    const n = k === '1e3' ? 1000 : Number(k);
    if (!Number.isFinite(n)) continue;
    const rgb = hexToRgb(v);
    if (!rgb) continue;
    entries.push({ n, rgb });
  }
  if (!entries.length) return out;
  entries.sort((a, b) => a.n - b.n);

  for (const stop of TAILWIND_STOPS) {
    const target = stop === '950' ? 950 : Number(stop);
    // Map Tailwind 950 onto the brand's 1000 stop when present (Archie uses 1000).
    const lookup = entries.some(e => e.n === 1000) && target === 950 ? 950 : target;
    if (lookup <= entries[0].n) {
      out.set(target, entries[0].rgb);
      continue;
    }
    if (lookup >= entries[entries.length - 1].n) {
      out.set(target, entries[entries.length - 1].rgb);
      continue;
    }
    let lo = entries[0];
    let hi = entries[entries.length - 1];
    for (let i = 0; i < entries.length - 1; i++) {
      if (entries[i].n <= lookup && entries[i + 1].n >= lookup) {
        lo = entries[i];
        hi = entries[i + 1];
        break;
      }
    }
    const t = (lookup - lo.n) / (hi.n - lo.n);
    const mix: RGB = [
      Math.round(lo.rgb[0] + (hi.rgb[0] - lo.rgb[0]) * t),
      Math.round(lo.rgb[1] + (hi.rgb[1] - lo.rgb[1]) * t),
      Math.round(lo.rgb[2] + (hi.rgb[2] - lo.rgb[2]) * t),
    ];
    out.set(target, mix);
  }
  return out;
}

function buildPaletteVars(prefix: string, palette: Record<string, string> | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  const stops = interpolatePalette(palette);
  for (const stop of TAILWIND_STOPS) {
    const rgb = stops.get(Number(stop));
    if (!rgb) continue;
    out[`--c-${prefix}-${stop}`] = tripletStr(rgb);
  }
  return out;
}

// Semantic UI tokens — picked per-brand to guarantee WCAG AA contrast on
// surface-default. Components consume these via Tailwind utilities (text-muted,
// bg-surface-muted, …) so we can tune contrast without touching every consumer.
type SemanticTokens = {
  textDefault: RGB;
  textMuted: RGB;
  textSubtle: RGB;
  textFaint: RGB;
  iconDefault: RGB;
  iconMuted: RGB;
  borderDefault: RGB;
  borderStrong: RGB;
  borderSoft: RGB;
  surfaceDefault: RGB;
  surfaceMuted: RGB;
  surfaceSubtle: RGB;
  surfaceBright: RGB;
  shadowColor: RGB;
  brandGold: RGB;
};

const MARTHA_SEMANTIC: SemanticTokens = {
  textDefault: [28, 25, 23],       // #1c1917 — n-900
  textMuted: [68, 64, 60],         // #44403c — n-700, ≥10:1 on white
  textSubtle: [107, 93, 82],       // #6B5D52 — tracker text-muted, ≥5.5:1
  textFaint: [163, 150, 134],      // #A39686 — tracker text-light
  iconDefault: [68, 64, 60],       // #44403c
  iconMuted: [107, 93, 82],        // #6B5D52
  borderDefault: [232, 224, 213],  // #E8E0D5
  borderStrong: [212, 203, 189],   // #D4CBBD
  borderSoft: [240, 235, 227],     // #F0EBE3 — tracker border-light
  surfaceDefault: [255, 255, 255], // #FFFFFF
  surfaceMuted: [251, 250, 248],   // #FBFAF8
  surfaceSubtle: [245, 241, 236],  // #F5F1EC
  surfaceBright: [253, 252, 251],  // #FDFCFB — tracker bg-subtle
  shadowColor: [47, 36, 27],       // #2F241B — warm shadow tint
  brandGold: [224, 182, 102],      // #E0B666 — Martha gold accent
};

const ARCHIE_SEMANTIC: SemanticTokens = {
  textDefault: [0, 6, 38],         // #000626 — Archie 1000
  textMuted: [74, 80, 112],        // #4a5070 — interp ~700, ≥7:1 on white
  textSubtle: [89, 95, 122],       // #595f7a — Archie 800
  textFaint: [136, 142, 168],      // #888ea8 — Archie ~n-500 interpolated
  iconDefault: [74, 80, 112],      // #4a5070
  iconMuted: [108, 115, 144],      // #6c7390 — between 600/800
  borderDefault: [214, 216, 229],  // #d6d8e5 — interp ~250
  borderStrong: [179, 184, 204],   // #b3b8cc — interp ~400
  borderSoft: [233, 235, 244],     // #e9ebf4 — Archie ~n-150 interpolated
  surfaceDefault: [255, 255, 255], // #FFFFFF
  surfaceMuted: [245, 246, 250],   // #f5f6fa — Archie n-100
  surfaceSubtle: [229, 231, 240],  // #e5e7f0 — Archie n-200
  surfaceBright: [250, 251, 253],  // #fafbfd — Archie ~n-50
  shadowColor: [0, 6, 38],         // #000626 — Archie deep blue tint
  brandGold: [224, 182, 102],      // #E0B666 — keep gold as universal accent
};

function buildSemanticVars(brand: string): Record<string, string> {
  const t = brand === 'archie' ? ARCHIE_SEMANTIC : MARTHA_SEMANTIC;
  return {
    '--c-text-default': tripletStr(t.textDefault),
    '--c-text-muted': tripletStr(t.textMuted),
    '--c-text-subtle': tripletStr(t.textSubtle),
    '--c-text-faint': tripletStr(t.textFaint),
    '--c-icon-default': tripletStr(t.iconDefault),
    '--c-icon-muted': tripletStr(t.iconMuted),
    '--c-border-default': tripletStr(t.borderDefault),
    '--c-border-strong': tripletStr(t.borderStrong),
    '--c-border-soft': tripletStr(t.borderSoft),
    '--c-surface-default': tripletStr(t.surfaceDefault),
    '--c-surface-muted': tripletStr(t.surfaceMuted),
    '--c-surface-subtle': tripletStr(t.surfaceSubtle),
    '--c-surface-bright': tripletStr(t.surfaceBright),
    '--c-shadow-color': tripletStr(t.shadowColor),
    '--c-brand-gold': tripletStr(t.brandGold),
  };
}

export const AppearanceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { preferences } = usePreferences();
  const { brand, platformVariant } = preferences.global;

  const brandProfile = useMemo<BrandProfileData>(() => {
    const key = `${brand}:${platformVariant === 'windows' ? 'web' : platformVariant}`;
    return BRAND_MAP[key] ?? MARTHA_WEB;
  }, [brand, platformVariant]);

  useEffect(() => {
    const root = document.documentElement;
    const colors = brandProfile.colors ?? {};

    const vars: Record<string, string> = {
      // Brand primary drives both Tailwind's `primary` and `coral` palettes
      // (the dashboard uses both interchangeably).
      ...buildPaletteVars('primary', colors.primary),
      ...buildPaletteVars('coral', colors.primary),
      ...buildPaletteVars('accent', colors.accent),
      ...buildPaletteVars('neutral', colors.neutral),
      ...buildSemanticVars(brand),
    };

    for (const [k, v] of Object.entries(vars)) {
      root.style.setProperty(k, v);
    }

    const bodyFont = brandProfile.typography?.body?.family;
    if (bodyFont) {
      root.style.setProperty('--brand-font', `${bodyFont}, system-ui, -apple-system, sans-serif`);
    } else {
      root.style.removeProperty('--brand-font');
    }

    root.dataset.brand = brand;
    root.dataset.platformVariant = platformVariant;

    return () => {
      // Clean up any vars we set so a subsequent provider mount starts fresh.
      for (const k of Object.keys(vars)) root.style.removeProperty(k);
      root.style.removeProperty('--brand-font');
    };
  }, [brand, platformVariant, brandProfile]);

  return (
    <PlatformProvider platform={platformVariant} brand={brandProfile}>
      {children}
    </PlatformProvider>
  );
};
